import React, {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import { ICallAdapter } from "../adapters/CallAdapter";
import {
  CMD_ID_DIRECTION,
  CMD_ID_NECK,
  CMD_ID_STOP,
  DIRECTION_DOWN,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_STOP,
  DIRECTION_UP,
  NECK_DOWN,
  NECK_LEFT,
  NECK_YAW_RESET,
  NECK_RIGHT,
  NECK_STOP,
  NECK_UP,
} from "@/common/cmd";
import { track } from "@/common/utils";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";

export enum EControlType {
  Direction,
  Neck,
}

const MoveController = ({
  type,
  adapter,
  isEnteredRoom,
}: {
  type: EControlType;
  adapter?: ICallAdapter;
  isEnteredRoom: boolean;
}) => {
  useEffect(() => {
    // 禁用右键菜单
    const handleContextMenu = (e: Event) => e.preventDefault();
    // 禁用长按行为（用于模拟长按时拦截）
    let touchTimer: any;

    const handleTouchStart = (e: TouchEvent) => {
      touchTimer = setTimeout(() => {
        e.preventDefault();
      }, 500); // 模拟长按
    };

    const handleTouchEnd = () => {
      clearTimeout(touchTimer);
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  const sendMsg = async (cmd: number, message: string, e?: KeyboardEvent) => {
    e?.preventDefault(); // 阻止默认行为（如浏览器快捷键）
    // 如果按键是重复触发（例如用户长按），就忽略
    if (e?.repeat) return;
    track("move", { cmd, message });
    console.log(`Sending command: ${cmd}, message: ${message}`);
    if (isEnteredRoom && adapter) {
      await adapter.sendMessage(cmd, message);
      console.log(`Sending command: ${cmd}, message: ${message}`);
    }
  };

  const handleLeft = () => {
    if (type === EControlType.Direction) {
      sendMsg(CMD_ID_DIRECTION, DIRECTION_LEFT);
      console.log("move left");
    } else {
      sendMsg(CMD_ID_NECK, NECK_LEFT);
      console.log("neck left");
    }
  };

  const handleRight = () => {
    if (type === EControlType.Direction) {
      sendMsg(CMD_ID_DIRECTION, DIRECTION_RIGHT);
      console.log("move right");
    } else {
      sendMsg(CMD_ID_NECK, NECK_RIGHT);
      console.log("neck right");
    }
  };

  const handleUp = () => {
    if (type === EControlType.Direction) {
      sendMsg(CMD_ID_DIRECTION, DIRECTION_UP);
      console.log("move up");
    } else {
      sendMsg(CMD_ID_NECK, NECK_UP);
      console.log("neck up");
    }
  };

  const handleDown = () => {
    if (type === EControlType.Direction) {
      sendMsg(CMD_ID_DIRECTION, DIRECTION_DOWN);
      console.log("move down");
    } else {
      sendMsg(CMD_ID_NECK, NECK_DOWN);
      console.log("neck down");
    }
  };

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hasSentCommand, setHasSentCommand] = useState(false);
  const [isLeftActive, setIsLeftActive] = useState(false);
  const [isRightActive, setIsRightActive] = useState(false);
  const [isUpActive, setIsUpActive] = useState(false);
  const [isDownActive, setIsDownActive] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const yawResetTimeoutRef = useRef<any>(null);

  const resetNeckYaw = () => {
    clearTimeout(yawResetTimeoutRef.current);
    // 3秒后reset
    yawResetTimeoutRef.current = setTimeout(() => {
      sendMsg(CMD_ID_NECK, NECK_YAW_RESET);
    }, 3000);
  };

  const handleDragMove = (e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });

    const threshold = 30;
    if (!hasSentCommand) {
      if (data.x < -threshold) {
        handleLeft();
        setIsLeftActive(true);
        setIsRightActive(false);
        setIsUpActive(false);
        setIsDownActive(false);
        setHasSentCommand(true);
      } else if (data.x > threshold) {
        handleRight();
        setIsRightActive(true);
        setIsLeftActive(false);
        setIsUpActive(false);
        setIsDownActive(false);
        setHasSentCommand(true);
      }
      if (data.y < -threshold) {
        handleUp();
        setIsUpActive(true);
        setIsLeftActive(false);
        setIsRightActive(false);
        setIsDownActive(false);
        setHasSentCommand(true);
      } else if (data.y > threshold) {
        handleDown();
        setIsDownActive(true);
        setIsLeftActive(false);
        setIsRightActive(false);
        setIsUpActive(false);
        setHasSentCommand(true);
      }
    }
  };

  const stopMove = () => {
    if (type === EControlType.Direction) {
      sendMsg(CMD_ID_STOP, DIRECTION_STOP);
      console.log("move stop");
    } else {
      sendMsg(CMD_ID_NECK, NECK_STOP);
      console.log("neck stop");
      if (isLeftActive || isRightActive) {
        resetNeckYaw();
      }
    }
  };

  const handleUpTouchStart = (e: React.TouchEvent | MouseEvent) => {
    e.preventDefault();
    handleUp();
    setIsUpActive(true);
  };

  const handleLeftTouchStart = (e: React.TouchEvent | MouseEvent) => {
    e.preventDefault();
    handleLeft();
    setIsLeftActive(true);
  };

  const handleDownTouchStart = (e: React.TouchEvent | MouseEvent) => {
    e.preventDefault();
    handleDown();
    setIsDownActive(true);
  };

  const handleRightTouchStart = (e: React.TouchEvent | MouseEvent) => {
    e.preventDefault();
    handleRight();
    setIsRightActive(true);
  };

  const handleEnd = () => {
    setPosition({ x: 0, y: 0 });
    stopMove();
    setHasSentCommand(false);
    setIsLeftActive(false);
    setIsRightActive(false);
    setIsUpActive(false);
    setIsDownActive(false);
  };

  return (
    <div className="flex flex-col items-center justify-center z-30">
      <div className="relative flex justify-center items-center ">
        <img
          src={require("@/assets/images/direction-bg.png")}
          alt="controller background"
          className="w-[8.125rem] h-[8.125rem] opacity-50 "
        />
        <div
          className={`absolute left-0 flex items-center w-[2.875rem] h-[6.75rem] ${
            isLeftActive ? "bg-direction-active-left" : ""
          } bg-contain bg-no-repeat bg-center`}
          onTouchStart={handleLeftTouchStart}
          onTouchEnd={handleEnd}
          onMouseDown={handleLeftTouchStart}
          onMouseUp={handleEnd}
        >
          <img
            src={require("@/assets/images/direction-left.png")}
            alt="left"
            className="w-2 h-2 ml-2"
          />
        </div>
        <div
          className={`absolute top-0 flex justify-center w-[6.75rem] h-[2.875rem] ${
            isUpActive ? "bg-direction-active-up" : ""
          } bg-contain bg-no-repeat bg-center`}
          onTouchStart={handleUpTouchStart}
          onTouchEnd={handleEnd}
          onMouseDown={handleUpTouchStart}
          onMouseUp={handleEnd}
        >
          <img
            src={require("@/assets/images/direction-up.png")}
            alt="up"
            className="w-2 h-2 mt-2"
          />
        </div>
        <div
          className={`absolute right-0 flex items-center justify-end w-[2.875rem] h-[6.75rem] ${
            isRightActive ? "bg-direction-active-right" : ""
          } bg-contain bg-no-repeat bg-center`}
          onTouchStart={handleRightTouchStart}
          onTouchEnd={handleEnd}
          onMouseDown={handleRightTouchStart}
          onMouseUp={handleEnd}
        >
          <img
            src={require("@/assets/images/direction-right.png")}
            alt="right"
            className="w-2 h-2 mr-2"
          />
        </div>
        <div
          className={`absolute bottom-0 flex justify-center items-end w-[6.75rem] h-[2.875rem] ${
            isDownActive ? "bg-direction-active-down" : ""
          } bg-contain bg-no-repeat bg-center`}
          onTouchStart={handleDownTouchStart}
          onTouchEnd={handleEnd}
          onMouseDown={handleDownTouchStart}
          onMouseUp={handleEnd}
        >
          <img
            src={require("@/assets/images/direction-down.png")}
            alt="down"
            className="w-2 h-2 mb-2"
          />
        </div>

        <Draggable
          bounds="parent"
          position={position}
          onDrag={handleDragMove}
          onStop={handleEnd}
        >
          <div
            ref={dragRef}
            className="absolute w-10 h-10 active:opacity-50 bg-white rounded-full cursor-move"
          />
        </Draggable>
      </div>
      <div className="text-white text-sm mt-3 landscape:hidden">
        {type === EControlType.Direction ? "行走控制" : "视角控制"}
      </div>
    </div>
  );
};

export default observer(MoveController);